import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { FiZap, FiArrowLeft, FiChevronRight } from "react-icons/fi";
import { useTitle } from "../../../hooks/useTitle";
import { useAuth } from "../../../services/useAuth";
import AppPage from "../../../components/CustomUI/AppPage";
import CardContainer from "../../../components/CustomUI/CardContainer";
import Button from "../../../components/BaseUI/Button";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import "./ProductionLines.scss";
import { withAuth } from "../../../components/HOC/withAuth";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

function ViewSubProductionLines() {
  const { id } = useParams();
  const navigate = useNavigate();
  useTitle("Rovion MES - Alt Üretim Hattı Detay");
  const { getToken, getUser } = useAuth();
  const currentUser = getUser();
  const allowedMachineIds = useMemo(() => {
    const list = currentUser?.machineAccess || [];
    if (!Array.isArray(list) || list.length === 0) return null;
    return new Set(list.map((x) => String(x)));
  }, [currentUser]);
  const [line, setLine] = useState(null);
  const [dailyQtyByOutputId, setDailyQtyByOutputId] = useState({});
  const [editedProduced, setEditedProduced] = useState({});
  const [savingLineId, setSavingLineId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState([]);
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const mountedRef = useRef(true);
  const lastRealtimeRefreshRef = useRef(0);

  const addToast = (title, descriptionOrType = "info", maybeType = "info") => {
    const knownTypes = ["success", "warning", "danger", "info"];
    const isLegacy = knownTypes.includes(descriptionOrType);
    const t = {
      id: Date.now(),
      title,
      description: isLegacy ? undefined : descriptionOrType,
      type: isLegacy ? descriptionOrType : maybeType,
    };
    setToasts((p) => [...p, t]);
    setTimeout(() => setToasts((p) => p.filter((x) => x.id !== t.id)), 3000);
  };

  const h = () => ({ Authorization: `Bearer ${getToken()}` });

  const fetchDailyProductionToday = useCallback(async () => {
    try {
      const start = new Date();
      start.setUTCHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setUTCHours(23, 59, 59, 999);
      const res = await axios.get(`${API_URL}/api/daily-production`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        params: { from: start.toISOString(), to: end.toISOString() },
      });
      const items = res.data?.items || [];
      const map = {};
      items.forEach((it) => {
        const oid = it.outputItemId?._id || it.outputItemId;
        if (!oid) return;
        const k = String(oid);
        map[k] = (map[k] || 0) + (Number(it.quantity) || 0);
      });
      setDailyQtyByOutputId(map);
    } catch {
      // noop
    }
  }, [getToken]);

  const fetchData = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const [linesRes] = await Promise.all([
        axios.get(
          `${API_URL}/api/production-lines/from-parts?subOnly=true`,
          {
            headers: h(),
          },
        ),
      ]);
      const lines = linesRes.data?.lines || linesRes.data || [];
      const found = (lines || []).find((l) => String(l._id) === String(id));
      setLine(found || null);
      await fetchDailyProductionToday();
    } catch {
      addToast("Veriler yüklenemedi", "danger");
    } finally {
      setLoading(false);
    }
  }, [id, getToken, fetchDailyProductionToday]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refreshRealtimeData = useCallback(async () => {
    const now = Date.now();
    if (now - lastRealtimeRefreshRef.current < 500) return;
    lastRealtimeRefreshRef.current = now;

    try {
      await fetchDailyProductionToday();
    } catch {
      // noop
    }
  }, [fetchDailyProductionToday, getToken]);

  useEffect(() => {
    if (loading) return undefined;

    const connect = () => {
      const token = getToken();
      if (!token) return;
      const apiUrl =
        process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";
      let wsUrl = apiUrl.replace(/^http/, "ws");
      if (!wsUrl.startsWith("ws://") && !wsUrl.startsWith("wss://")) {
        wsUrl = `ws://${wsUrl.replace(/^https?:\/\//, "")}`;
      }
      const wsPath = wsUrl.endsWith("/") ? "ws" : "/ws";
      const ws = new WebSocket(`${wsUrl}${wsPath}?token=${token}`);
      wsRef.current = ws;
      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg?.type === "telemetry" && msg?.deviceId === "machine_update") {
            refreshRealtimeData();
          }
        } catch {
          // noop
        }
      };
      ws.onclose = () => {
        if (!mountedRef.current) return;
        reconnectTimerRef.current = setTimeout(connect, 2000);
      };
    };

    connect();

    const pollId = setInterval(() => {
      refreshRealtimeData();
    }, 60000);

    return () => {
      clearInterval(pollId);
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      const ws = wsRef.current;
      if (
        ws &&
        (ws.readyState === WebSocket.OPEN ||
          ws.readyState === WebSocket.CONNECTING)
      ) {
        ws.close(1000, "ViewSubProductionLines cleanup");
      }
      wsRef.current = null;
    };
  }, [loading, refreshRealtimeData, getToken]);

  const getMachineLabel = (mi) => {
    const m = mi.machineId || {};
    if (m.name && m.code) return `${m.name} (${m.code})`;
    return m.name || m.code || "Makine";
  };

  const lineForView = useMemo(() => {
    if (!line) return null;
    if (!allowedMachineIds) return line;
    const machineItems = (line.machineItems || []).filter((mi) => {
      const mid = mi.machineId?._id || mi.machineId;
      return mid && allowedMachineIds.has(String(mid));
    });
    return { ...line, machineItems };
  }, [line, allowedMachineIds]);

  const formatLineItemsInputs = (mi, showStock = true) => {
    const items = mi.lineItems || [];
    if (items.length === 0) {
      const p = mi.entryPartId;
      if (p) return `${p.name || p.code || "Parça"} × ${Number(mi.inputAmount) || 1}`;
      return "—";
    }
    return items
      .map((li) => {
        const part = li.partId;
        const qty = Number(li.quantityUsed) || 0;
        const label = part
          ? part.name && part.code
            ? `${part.name} (${part.code})`
            : part.name || part.code || "Parça"
          : "Parça";
        return `${label} × ${qty}`;
      })
      .filter(Boolean)
      .join(", ");
  };

  const getProducedForMachine = (idx) => {
    const key = `${lineForView?._id}-${idx}`;
    const mi = (lineForView?.machineItems || [])[idx];
    const fromServer = mi?.producedCount != null ? Number(mi.producedCount) : 0;
    const base = editedProduced[key] != null ? editedProduced[key] : fromServer;
    return Math.max(0, parseInt(base, 10) || 0);
  };

  const handleProducedChange = (idx, val) => {
    if (!lineForView?._id) return;
    setEditedProduced((p) => ({ ...p, [`${lineForView._id}-${idx}`]: val }));
  };

  const getTodayRecordedForStep = (mi) => {
    const oid = mi.outputItemId ? String(mi.outputItemId) : null;
    if (!oid) return 0;
    return dailyQtyByOutputId[oid] ?? 0;
  };

  const saveProducedCounts = async () => {
    if (!lineForView) return;
    const machines = lineForView.machineItems || [];
    const entries = machines
      .map((_, idx) => ({ idx, qty: getProducedForMachine(idx) }))
      .filter((x) => (Number(x.qty) || 0) > 0);
    setSavingLineId(lineForView._id);
    try {
      await Promise.all(
        entries.map((e) =>
          axios.post(
            `${API_URL}/api/production/record`,
            {
              lineId: lineForView.id || `part:${lineForView._id}`,
              stepIndex: e.idx,
              producedNow: e.qty,
            },
            { headers: h() },
          ),
        ),
      );
      setEditedProduced((p) => {
        const next = { ...p };
        entries.forEach((e) => delete next[`${lineForView._id}-${e.idx}`]);
        return next;
      });
      await fetchDailyProductionToday();
      addToast("Kaydedildi", "success");
    } catch (err) {
      addToast(
        "Hata",
        err.response?.data?.message || "Kaydetme başarısız",
        "danger",
      );
    } finally {
      setSavingLineId(null);
    }
  };

  return (
    <AppPage
      title="Alt Üretim Hattı Detay"
      subtitle={lineForView ? lineForView.name : ""}
      icon={<FiZap />}
      actions={
        <Button
          appearance="danger-subtle"
          onClick={() => navigate("/production-lines/sub")}
          icon={<FiArrowLeft />}
        >
          Geri
        </Button>
      }
    >
      <CardContainer>
        {loading ? (
          <div className="loading-container">
            <FiZap className="spin" size={48} />
            <p>Yükleniyor...</p>
          </div>
        ) : !lineForView ? (
          <div className="empty-state">
            <FiZap size={64} />
            <p>Alt üretim hattı bulunamadı</p>
            <Button
              appearance="primary"
              onClick={() => navigate("/production-lines/sub")}
            >
              Listeye Dön
            </Button>
          </div>
        ) : (
          <>
            <div className="pl-visual">
              <div className="pl-lane pl-lane--steps pl-lane--active">
                <div className="pl-lane-header">
                  <span className="pl-name">{lineForView.name}</span>
                  <span className="pl-code">{lineForView.code}</span>
                  <span className="pl-total-badge">
                    Toplam:{" "}
                    {(lineForView.machineItems || []).reduce(
                      (s, _, i) => s + getProducedForMachine(i),
                      0,
                    )}{" "}
                    adet
                  </span>
                  <span className="pl-badge pl-type-production">Alt Ürün</span>
                  <span
                    className={`pl-badge pl-entry-${lineForView.dataEntryType || "manual"}`}
                  >
                    {(lineForView.dataEntryType || "manual") === "from_machine"
                      ? "Makineden"
                      : "Manuel"}
                  </span>
                </div>
                <div className="pl-steps">
                  <div className="pl-steps-pipeline">
                    {(lineForView.machineItems || []).map((mi, idx) => (
                      <React.Fragment key={idx}>
                        <div className="pl-step-card">
                          <div className="pl-step-card-head">
                            <span className="pl-step-num">{idx + 1}</span>
                            <span className="pl-step-machine">
                              {getMachineLabel(mi)}
                            </span>
                          </div>
                          <div className="pl-step-card-body">
                            <label className="pl-step-label">Üretilen</label>
                            <div className="pl-step-input-wrap">
                              <input
                                type="number"
                                min={0}
                                className="pl-produced-input"
                                value={
                                  editedProduced[`${lineForView._id}-${idx}`] ??
                                  (getProducedForMachine(idx) || "")
                                }
                                onChange={(e) =>
                                  handleProducedChange(idx, e.target.value)
                                }
                              />
                              <span className="pl-step-unit">adet</span>
                            </div>
                            <div className="pl-step-today-recorded text-muted small mt-1">
                              Bugün kayıtlı: {getTodayRecordedForStep(mi)} adet
                            </div>
                            <div className="pl-step-continuity mt-2">
                              <div className="pl-step-row">
                                <span className="pl-step-sublabel">
                                  Girdiler
                                </span>
                                <span className="pl-step-continuity-value">
                                  {formatLineItemsInputs(mi)}
                                </span>
                              </div>
                              <div className="pl-step-row">
                                <span className="pl-step-sublabel">Çıktı</span>
                                <span className="pl-step-continuity-value">
                                  {(() => {
                                    const p = mi.outputPartId;
                                    if (!p) return "—";
                                    return p.name && p.code
                                      ? `${p.name} (${p.code})`
                                      : p.name || p.code || "Parça";
                                  })()}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        {idx < (lineForView.machineItems?.length || 0) - 1 && (
                          <div className="pl-step-connector">
                            <FiChevronRight />
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                  <div className="pl-step-actions">
                    <Button
                      appearance="primary-animated"
                      onClick={saveProducedCounts}
                      disabled={savingLineId === lineForView._id}
                    >
                      {savingLineId === lineForView._id
                        ? "Kaydediliyor..."
                        : "Kaydet"}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </CardContainer>
      <ToastContainer>
        {toasts.map((t) => (
          <Toast
            key={t.id}
            title={t.title}
            description={t.description}
            appearance={t.type}
            onClose={() => setToasts((p) => p.filter((x) => x.id !== t.id))}
          />
        ))}
      </ToastContainer>
    </AppPage>
  );
}

export default withAuth(ViewSubProductionLines, ["admin", "employee", "user"]);
